import * as React from 'react'
import { Link, useParams } from 'react-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  CloudOff,
  Crosshair,
  History,
  Loader2,
  LocateFixed,
  Plus,
  TriangleAlert,
} from 'lucide-react'
import type {
  IrrigationEquipmentDto,
  IrrigationProblemDto,
  IrrigationSiteDto,
  IrrigationTimerDto,
  WaterSourceDto,
  ZoneDto,
} from '@lib/dto/irrigation.dto'
import {
  equipmentBoxId,
  problemEquipmentId,
  problemTimerId,
  problemWaterSourceId,
  problemZoneId,
} from '@lib/dto/irrigation.dto'
import { parseUuid } from '@lib/validators/uuid.validator'
import {
  BOUNDARY_MIN_VERTICES,
  isRecordedPoint,
  type GeoPoint,
} from '@lib/dto/geo.dto'
import { isPointInRing, polygonAreaSquareMeters } from '@lib/helpers/geo'
import { BOUNDARY_PROBLEM_MESSAGES } from '@lib/validators/polygon.validator'
import type { IrrigationProblemTypeDto } from '@lib/dto/irrigation-problem-type.dto'
import type { SiteDto } from '@lib/dto/site.dto'
import {
  IRRIGATION_EQUIPMENT_KIND_LABELS,
  type IrrigationProblemSubject,
} from '@lib/enums/irrigation.enum'
import { createSiteMap } from '@/map/basemap'
import { MANUAL_PLACEMENT_THRESHOLD_M } from '@/map/placement'
import { MapZoomControls } from '@/map/MapZoomControls'
import { BoundaryLayer } from '@/map/BoundaryLayer'
import { BoundaryDraftBar } from '@/map/BoundaryDraftBar'
import { RegionLabelLayer, type RegionLabelSpec } from '@/map/RegionLabelLayer'
import { regionColors } from '@/map/regionColors'
import { REGION_FILL, REGION_STROKE } from '@/map/regionClasses'
import { ChangeAnswers } from '@/site-visits/ChangeAnswers'
import {
  addQuestion,
  editQuestion,
  moveQuestion,
  zoneSnapshot,
  type ChangeQuestion,
} from '@/site-visits/siteRecordChanges'
import { TRACKED, placedRecord } from '@/irrigation-survey/changeQuestions'
import { useBoundaryDraft, type BoundaryDraft } from '@/map/useBoundaryDraft'
import { useSite } from '@/hooks/useSite'
import { useAreas } from '@/hooks/useAreas'
import { useIrrigationProblemTypes } from '@/hooks/useIrrigationProblemTypes'
import {
  useIrrigationSurvey,
  type IrrigationSurvey,
} from '@/hooks/useIrrigationSurvey'
import { useMediaQuery, DESKTOP_QUERY } from '@/hooks/useMediaQuery'
import {
  ACCURACY_WARN_M,
  useGeolocation,
  type Fix,
} from '@/field-map/useGeolocation'
import {
  appliesToOf,
  type IrrigationChange,
} from '@/irrigation-survey/irrigationOperations'
import {
  newEquipment,
  newProblem,
  newTimer,
  newWaterSource,
  pinOfRecord,
  type Pin,
} from '@/irrigation-survey/irrigationDrafts'
import {
  equipmentGlyph,
  zoneName,
  zonesByTimer,
  zonesInOrder,
} from '@/irrigation-survey/irrigationLabels'
import {
  MARKER_SIZE_PX,
  irrigationMarkerHtml,
  irrigationMarkerSignature,
  type IrrigationMarkerProps,
  type IrrigationMarkerTone,
} from '@/irrigation-survey/IrrigationMarker'
import {
  CLUSTER_SIZE_PX,
  irrigationClusterHtml,
  type ClusterMember,
} from '@/irrigation-survey/IrrigationCluster'
import {
  RecordEditor,
  parentOf,
  sheetTitle,
  type EditorContext,
  type PlaceKind,
  type PlaceRequest,
  type SheetTarget,
} from '@/irrigation-survey/IrrigationEditors'
import { SystemBar } from '@/irrigation-survey/SystemChecklist'
import { RepairEstimate } from '@/irrigation-survey/RepairEstimate'
import { useSiteVisits } from '@/site-visits/useSiteVisits'
import { isSameVisit } from '@/site-visits/visitStore'
import { useQuery } from '@tanstack/react-query'
import { siteVisitService } from '@/services/SiteVisitService'
import { siteVisitKeys } from '@/hooks/queryKeys'
import type { SiteMapAsOfDto } from '@lib/dto/site-history.dto'
import {
  useLastVisitProblems,
  type LastVisit,
} from '@/site-visits/useLastVisit'
import { type IrrigationLastVisit } from '@/irrigation-survey/LastVisitProblems'
import { isBroughtForward } from '@/site-visits/lastVisit'
import { VisitBar, canRecordIn } from '@/site-visits/VisitBar'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useOrganization } from '@/hooks/useOrganization'
import { BidTotalButton } from '@/bid-total/BidTotalButton'
import { IrrigationBidTray } from '@/bid-total/IrrigationBidTray'
import { useBidTray } from '@/bid-total/useBidTray'
import { useIrrigationBidTotal } from '@/bid-total/useIrrigationBidTotal'

/**
 * How near, in screen pixels, pins are gathered into one cluster: a little
 * more than a pin is wide, so only pins that would cover or touch each other
 * are, and pins with room between them stay as they are.
 */
const CLUSTER_RADIUS_PX = MARKER_SIZE_PX + 6

/** Last visit, as a sheet's section takes it: the day, and findings when known. */
function sheetLastVisit(
  last: LastVisit<readonly IrrigationProblemDto[]> | null,
): IrrigationLastVisit | null {
  if (last === null) return null
  const startedOn = last.visit.startedOn
  if (last.status === 'ready') {
    return { status: 'ready', startedOn, problems: last.findings }
  }
  return { status: last.status, startedOn }
}

const PLACE_LABELS: Record<PlaceKind, string> = {
  waterSource: 'Water source',
  timer: 'Timer',
  problem: 'Problem at a spot',
  VALVE: IRRIGATION_EQUIPMENT_KIND_LABELS.VALVE,
  VALVE_BOX: IRRIGATION_EQUIPMENT_KIND_LABELS.VALVE_BOX,
  MASTER_VALVE: IRRIGATION_EQUIPMENT_KIND_LABELS.MASTER_VALVE,
  BACKFLOW: IRRIGATION_EQUIPMENT_KIND_LABELS.BACKFLOW,
  BALL_VALVE: IRRIGATION_EQUIPMENT_KIND_LABELS.BALL_VALVE,
  SENSOR: IRRIGATION_EQUIPMENT_KIND_LABELS.SENSOR,
  OTHER: 'Something else',
}

/**
 * What the Add menu offers. A water source starts a system; the rest of the
 * system (ball valve, backflow, master valve, timers, sensors) is placed
 * from its source's steps, in the order the water runs, so it is never
 * dropped without knowing what it is on. What is found while walking a zone
 * is placed from here, and the zone's outline drawn: its shape is seen only
 * once it runs, out in the yard, not from the timer whose sheet lists it.
 * A valve box comes first there, because that is how valves are found: the
 * box is opened and what is in it is said on its sheet. A lone valve is for
 * one with no box, such as an anti-siphon valve above ground.
 */
const ADD_GROUPS: readonly {
  title: string
  hint?: string
  kinds: readonly PlaceKind[]
  /** Also offers drawing the outline of the zone being walked. */
  zoneOutline?: true
}[] = [
  {
    title: 'The system',
    hint: 'Its ball valve, backflow, master valve and timers follow from the water source, in order.',
    kinds: ['waterSource'],
  },
  {
    title: 'Walking a zone',
    kinds: ['problem', 'VALVE_BOX', 'VALVE', 'OTHER'],
    zoneOutline: true,
  },
]

/** A site route whose id is not an id. The survey must not mount for it. */
function MissingSite() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 bg-app px-6 text-center">
      <p className="text-sm font-medium">Site not found</p>
      <p className="text-sm text-muted-text">
        It may have been removed, or the link may be wrong.
      </p>
      <Button variant="outline" asChild>
        <Link to="/clients">Back to clients</Link>
      </Button>
    </div>
  )
}

/**
 * The irrigation survey: walk a sprinkler system and record what is there and
 * what is wrong with it. See docs/irrigation.md.
 *
 * Keyed by site below, because the survey reads a site's local copy once,
 * when it mounts (see `useIrrigationSurvey`).
 */
/** The bid tray's id, which the header button controls. */
const BID_TRAY_ID = 'irrigation-bid-total'

export function IrrigationMapPage() {
  const params = useParams<{ siteId: string }>()
  const siteId = parseUuid(params.siteId)
  const site = useSite(siteId ?? '')
  const problemTypes = useIrrigationProblemTypes()
  const types = React.useMemo(
    () => problemTypes.data ?? [],
    [problemTypes.data],
  )
  const appliesTo = React.useMemo(() => appliesToOf(types), [types])
  if (siteId === null) return <MissingSite />
  return (
    <IrrigationSurveyScreen
      key={siteId}
      siteId={siteId}
      site={site.data}
      problemTypes={types}
      appliesTo={appliesTo}
    />
  )
}

interface ScreenProps {
  siteId: string
  site: SiteDto | undefined
  problemTypes: readonly IrrigationProblemTypeDto[]
  appliesTo: ReadonlyMap<string, readonly IrrigationProblemSubject[]>
}

type SurveyPinProps = Omit<IrrigationMarkerProps, 'selected' | 'pending'>

/**
 * One pin, with its sheet target typed at the call.
 *
 * Built here rather than inside `.map()`, whose callback is not contextually
 * typed by the array it is spread into. Without that, the entity and tone
 * literals widen to `string` and only an assertion puts them back.
 */
function placedPin(
  key: string,
  target: SheetTarget,
  latitude: number,
  longitude: number,
  props: SurveyPinProps,
): {
  key: string
  target: SheetTarget
  latitude: number
  longitude: number
  props: SurveyPinProps
} {
  return { key, target, latitude, longitude, props }
}

/** Where a record of each kind stands, for the pins and for panning to one. */
function pinsOf(site: IrrigationSiteDto): {
  key: string
  target: SheetTarget
  latitude: number
  longitude: number
  props: SurveyPinProps
}[] {
  const openProblemOn = new Set(
    site.problems
      .filter(
        (problem) => problem.fixedAt === null && problem.position === null,
      )
      .flatMap((problem) => [
        problemTimerId(problem),
        problemEquipmentId(problem),
        problemWaterSourceId(problem),
      ])
      .filter((id): id is string => id !== null),
  )
  // What is in a box has no pin of its own, so the box shows its problems.
  for (const item of site.equipment) {
    const boxId = equipmentBoxId(item)
    if (boxId !== null && openProblemOn.has(item.id)) openProblemOn.add(boxId)
  }
  return [
    ...site.waterSources.map((record) =>
      waterSourcePin(record, openProblemOn.has(record.id)),
    ),
    ...site.timers.map((record) =>
      timerPin(record, openProblemOn.has(record.id)),
    ),
    // Well components stand at their well, and what is in a valve box at the
    // box; they are listed on the well's or the box's sheet.
    ...site.equipment
      .filter(
        (record) =>
          record.kind !== 'WELL_COMPONENT' && equipmentBoxId(record) === null,
      )
      .map((record) =>
        equipmentPin(record, site, openProblemOn.has(record.id)),
      ),
    ...site.problems.flatMap((record) => {
      const position = record.position
      if (position === null || record.fixedAt !== null) return []
      return [problemPin(record, position)]
    }),
  ]
}

function waterSourcePin(record: WaterSourceDto, hasOpenProblem: boolean) {
  return placedPin(
    `waterSource:${record.id}`,
    { entity: 'waterSource', record, isNew: false },
    record.latitude,
    record.longitude,
    { tone: 'waterSource', hasOpenProblem },
  )
}

function timerPin(record: IrrigationTimerDto, hasOpenProblem: boolean) {
  return placedPin(
    `timer:${record.id}`,
    { entity: 'timer', record, isNew: false },
    record.latitude,
    record.longitude,
    { tone: 'timer', hasOpenProblem },
  )
}

function equipmentPin(
  record: IrrigationEquipmentDto,
  site: IrrigationSiteDto,
  hasOpenProblem: boolean,
) {
  return placedPin(
    `equipment:${record.id}`,
    { entity: 'equipment', record, isNew: false },
    record.latitude,
    record.longitude,
    {
      tone: record.kind === 'VALVE_BOX' ? 'valveBox' : 'equipment',
      glyph: equipmentGlyph(record, site),
      hasOpenProblem,
    },
  )
}

function problemPin(
  record: IrrigationProblemDto,
  position: NonNullable<IrrigationProblemDto['position']>,
) {
  return placedPin(
    `problem:${record.id}`,
    { entity: 'problem', record, isNew: false },
    position.latitude,
    position.longitude,
    { tone: 'problem', hasOpenProblem: false },
  )
}

const TONE_NOUNS: Record<IrrigationMarkerTone, string> = {
  waterSource: 'water source',
  timer: 'timer',
  equipment: 'part',
  valveBox: 'valve box',
  problem: 'problem',
}

/** What a pin is, in words, for the cluster that may gather it. */
function pinNoun(pin: { target: SheetTarget; props: SurveyPinProps }): string {
  if (pin.props.faint === true) return "last visit's problem"
  if (pin.target.entity === 'equipment') {
    const { kind } = pin.target.record
    return kind === 'OTHER'
      ? 'other part'
      : IRRIGATION_EQUIPMENT_KIND_LABELS[kind].toLowerCase()
  }
  return TONE_NOUNS[pin.props.tone]
}

/**
 * Last visit's problems at a spot, faint, so the riser that was broken last
 * year can be found and checked (docs/site-visits.md). One brought forward
 * is drawn as this visit's instead, and one whose zone is gone has nowhere
 * to be checked. A tap opens the zone, whose sheet has Still here.
 */
function lastVisitPinsOf(
  lastProblems: readonly IrrigationProblemDto[],
  site: IrrigationSiteDto,
): ReturnType<typeof pinsOf> {
  return lastProblems.flatMap((problem) => {
    const { position } = problem
    const zone = site.zones.find(
      (candidate) => candidate.id === problemZoneId(problem),
    )
    if (
      position === null ||
      zone === undefined ||
      isBroughtForward(problem, site.problems)
    ) {
      return []
    }
    return [
      placedPin(
        `last:${problem.id}`,
        { entity: 'zone', record: zone, isNew: false, showLastVisit: true },
        position.latitude,
        position.longitude,
        { tone: 'problem', hasOpenProblem: false, faint: true },
      ),
    ]
  })
}

/**
 * The pins as they stood on an earlier visit: the system's parts where they
 * stood then, those added since left off; problems where they are. Drawing
 * only: each pin still opens the record as it stands.
 */
function pinsAsOf(
  pins: ReturnType<typeof pinsOf>,
  asOf: SiteMapAsOfDto,
): ReturnType<typeof pinsOf> {
  const then = new Map<string, { latitude: number; longitude: number }>([
    ...asOf.waterSources
      .filter(isRecordedPoint)
      .map((item) => [`waterSource:${item.id}`, item] as const),
    ...asOf.timers
      .filter(isRecordedPoint)
      .map((item) => [`timer:${item.id}`, item] as const),
    ...asOf.equipment
      .filter(isRecordedPoint)
      .map((item) => [`equipment:${item.id}`, item] as const),
  ])
  return pins.flatMap((pin) => {
    if (pin.target.entity === 'problem') return [pin]
    const was = then.get(pin.key)
    return was === undefined
      ? []
      : [{ ...pin, latitude: was.latitude, longitude: was.longitude }]
  })
}

/**
 * Parts that stood on an earlier visit and have come out since, where they
 * stood: drawn faint, and not opened, since there is nothing left to change.
 */
function goneSinceOf(
  asOf: SiteMapAsOfDto,
  site: IrrigationSiteDto,
): {
  key: string
  latitude: number
  longitude: number
  tone: 'waterSource' | 'timer' | 'equipment' | 'valveBox'
  glyph?: string
}[] {
  const live = new Set([
    ...site.waterSources.map((item) => item.id),
    ...site.timers.map((item) => item.id),
    ...site.equipment.map((item) => item.id),
  ])
  return [
    ...asOf.waterSources
      .filter(isRecordedPoint)
      .filter((item) => !live.has(item.id))
      .map((item) => ({
        ...item,
        key: `gone:${item.id}`,
        tone: 'waterSource' as const,
      })),
    ...asOf.timers
      .filter(isRecordedPoint)
      .filter((item) => !live.has(item.id))
      .map((item) => ({
        ...item,
        key: `gone:${item.id}`,
        tone: 'timer' as const,
      })),
    ...asOf.equipment
      .filter(isRecordedPoint)
      .filter(
        (item) =>
          !live.has(item.id) &&
          item.kind !== 'WELL_COMPONENT' &&
          item.boxId === null,
      )
      .map((item) => ({
        key: `gone:${item.id}`,
        latitude: item.latitude,
        longitude: item.longitude,
        tone:
          item.kind === 'VALVE_BOX'
            ? ('valveBox' as const)
            : ('equipment' as const),
        glyph: equipmentGlyph(item, asOf),
      })),
  ]
}

/**
 * The first zoom deeper than the map's at which pins spread wider than a
 * cluster gathers, so a cluster of them parts there. Null for pins that
 * would not part at any zoom the map allows, because they stand on one spot
 * or as good as: those are fanned out instead.
 */
function partingZoom(map: L.Map, at: readonly L.LatLng[]): number | null {
  for (
    let zoom = Math.floor(map.getZoom()) + 1;
    zoom <= map.getMaxZoom();
    zoom += 1
  ) {
    const size = L.bounds(at.map((point) => map.project(point, zoom))).getSize()
    if (Math.hypot(size.x, size.y) > CLUSTER_RADIUS_PX) return zoom
  }
  return null
}

/** A zone's outline as drawn: its id, its number and its ring. */
interface DrawnZone {
  id: string
  number: number
  ring: GeoPoint[]
}

/**
 * The zone a tap inside an outline means. Zones may overlap, so of those
 * under the tap it is the smallest: a bed inside a lawn's zone is reached by
 * tapping the bed, and the lawn by tapping anywhere else on it.
 */
function zoneAt(
  point: GeoPoint,
  zones: readonly DrawnZone[],
): DrawnZone | null {
  let found: { zone: DrawnZone; size: number } | null = null
  for (const zone of zones) {
    if (!isPointInRing(point, zone.ring)) continue
    const size = polygonAreaSquareMeters(zone.ring)
    if (found === null || size < found.size) found = { zone, size }
  }
  return found?.zone ?? null
}

/** The map center as a pin, and whether a person moved it off the live fix. */
function pinAtCenter(map: L.Map, fix: Fix | null): Pin {
  const center = map.getCenter()
  const driftM =
    fix === null
      ? Number.POSITIVE_INFINITY
      : map.distance(center, L.latLng(fix.latitude, fix.longitude))
  const placedByHand = driftM > MANUAL_PLACEMENT_THRESHOLD_M
  return {
    latitude: center.lat,
    longitude: center.lng,
    accuracyM: placedByHand || fix === null ? null : fix.accuracyM,
    positionSource: placedByHand ? 'MANUAL' : 'GPS',
  }
}

/**
 * The change that saves a just-dropped record, or null for one that needs
 * finishing first: a problem needs its kind, and "something else" its name.
 */
function savedOnDrop(target: SheetTarget): IrrigationChange | null {
  switch (target.entity) {
    case 'waterSource':
      return { kind: 'upsert', entity: 'waterSource', record: target.record }
    case 'timer':
      return { kind: 'upsert', entity: 'timer', record: target.record }
    case 'equipment':
      return target.record.kind === 'OTHER'
        ? null
        : { kind: 'upsert', entity: 'equipment', record: target.record }
    case 'zones':
    case 'zone':
    case 'problem':
      return null
  }
}

/** What to do next while a zone's outline is drawn. */
function outlineHint(draft: BoundaryDraft): string {
  if (draft.mode === 'drawing') {
    return 'Tap the map around the ground this zone waters. It may overlap other zones.'
  }
  return 'Drag a point to reshape it · tap a faint point to add one'
}

/** Whether a record stands at its own pin, and so can be moved. */
function hasOwnPin(target: SheetTarget): boolean {
  switch (target.entity) {
    case 'waterSource':
    case 'timer':
      return true
    case 'equipment':
      // A well component stands at its well and moves with the well, and
      // what is in a valve box with the box.
      return (
        target.record.kind !== 'WELL_COMPONENT' &&
        equipmentBoxId(target.record) === null
      )
    case 'problem':
      return target.record.position !== null
    case 'zones':
    case 'zone':
      return false
  }
}

/**
 * The change that moves a record's pin. Placed by hand, with no accuracy: a
 * pin a person dragged onto the imagery is as good as the imagery, and that
 * is a different claim from a GPS fix (see "Position is two different claims"
 * in docs/field-survey.md).
 */
function movedTo(
  target: SheetTarget,
  at: { latitude: number; longitude: number },
): IrrigationChange | null {
  const pin: Pin = { ...at, accuracyM: null, positionSource: 'MANUAL' }
  // Stamped as a sheet's Save stamps it, for the copy on this phone.
  const updatedAt = new Date().toISOString()
  switch (target.entity) {
    case 'waterSource':
      return {
        kind: 'upsert',
        entity: 'waterSource',
        record: { ...target.record, ...pin, updatedAt },
      }
    case 'timer':
      return {
        kind: 'upsert',
        entity: 'timer',
        record: { ...target.record, ...pin, updatedAt },
      }
    case 'equipment':
      return {
        kind: 'upsert',
        entity: 'equipment',
        record: { ...target.record, ...pin, updatedAt },
      }
    case 'problem':
      return {
        kind: 'upsert',
        entity: 'problem',
        record: { ...target.record, position: pin, updatedAt },
      }
    case 'zones':
    case 'zone':
      return null
  }
}

/** The key a sheet's record is drawn under. */
/**
 * The pin a sheet's record is shown by: its own, or for something in a valve
 * box, the box's, since it has none of its own there.
 */
function targetKey(target: SheetTarget | null): string | null {
  if (target === null) return null
  if (target.entity === 'equipment') {
    const boxId = equipmentBoxId(target.record)
    if (boxId !== null) return `equipment:${boxId}`
  }
  return `${target.entity}:${target.record.id}`
}

/** The saved record a sheet shows, refreshed from the survey; null once it is gone. */
function liveTarget(
  target: SheetTarget | null,
  site: IrrigationSiteDto,
): SheetTarget | null {
  if (target === null || target.isNew) return target
  const { id } = target.record
  switch (target.entity) {
    case 'waterSource': {
      const record = site.waterSources.find((item) => item.id === id)
      return record === undefined
        ? null
        : { entity: 'waterSource', record, isNew: false }
    }
    case 'timer': {
      const record = site.timers.find((item) => item.id === id)
      return record === undefined
        ? null
        : { entity: 'timer', record, isNew: false }
    }
    case 'zones': {
      const record = site.timers.find((item) => item.id === id)
      return record === undefined
        ? null
        : { entity: 'zones', record, isNew: false }
    }
    case 'zone': {
      const record = site.zones.find((item) => item.id === id)
      return record === undefined
        ? null
        : {
            entity: 'zone',
            record,
            isNew: false,
            showLastVisit: target.showLastVisit === true,
          }
    }
    case 'equipment': {
      const record = site.equipment.find((item) => item.id === id)
      return record === undefined
        ? null
        : { entity: 'equipment', record, isNew: false }
    }
    case 'problem': {
      const record = site.problems.find((item) => item.id === id)
      return record === undefined
        ? null
        : { entity: 'problem', record, isNew: false }
    }
  }
}

function IrrigationSurveyScreen({
  siteId,
  site,
  problemTypes,
  appliesTo,
}: ScreenProps) {
  const siteVisits = useSiteVisits(siteId)
  /** An earlier visit somebody chose to look at; null for the newest. */
  const [viewedVisitId, setViewedVisitId] = React.useState<string | null>(null)
  const newest = siteVisits.current('irrigation')
  const viewed =
    viewedVisitId === null
      ? null
      : (siteVisits
          .all('irrigation')
          .find((visit) => isSameVisit(visit, viewedVisitId)) ?? null)
  /*
   * An earlier visit, chosen from the visit bar: its problems, read or (once
   * somebody chooses Edit it) edited, on the system as it stood then. The
   * system itself is only changed as it stands now (docs/site-visits.md).
   */
  const earlierId =
    viewed !== null && newest !== null && !isSameVisit(newest, viewed.id)
      ? viewed.id
      : null
  const viewingEarlier = earlierId !== null
  // The visit whose problems the map shows: the newest that looked at
  // irrigation, or the earlier one chosen.
  const working = viewingEarlier ? viewed : newest
  // Where things stood on the earlier visit shown, from the site's history.
  const asOfQuery = useQuery({
    queryKey: siteVisitKeys.map(siteId, earlierId ?? 'none'),
    queryFn: () => siteVisitService.mapAsOf(earlierId ?? ''),
    enabled: earlierId !== null,
    retry: false,
  })
  const asOf = earlierId === null ? null : (asOfQuery.data ?? null)
  /** An earlier visit somebody chose to carry on with; see `canRecordIn`. */
  const [unlockedVisitId, setUnlockedVisitId] = React.useState<string | null>(
    null,
  )
  const canRecordInVisit = canRecordIn(working, unlockedVisitId)
  const survey = useIrrigationSurvey(siteId, appliesTo, {
    visit:
      working === null
        ? null
        : {
            id: working.id,
            joinedIds: working.joinedIds,
            pending: working.pending,
          },
    canRecord: canRecordInVisit,
    canSend: siteVisits.isSent,
  })
  // And only once the problems shown are this visit's own.
  const canRecordFindings = canRecordInVisit && survey.findingsReady
  /*
   * On a revisit, a change to the system that could be either asks which it
   * was: the site changing, or last visit's record being wrong
   * (docs/site-visits.md).
   */
  const isRevisit = siteVisits.isRevisit(
    'irrigation',
    canRecordInVisit ? (working?.id ?? null) : null,
  )
  // A change made in no visit asks when it happened too: no visit dates it.
  const asksWhen = isRevisit && !canRecordInVisit
  /*
   * What the visit before this one found, for each sheet's problems to show
   * and bring forward with Still here, and for the map to draw faint. Read
   * only while there is a visit to bring anything into.
   */
  const lastVisit = useLastVisitProblems(
    siteId,
    canRecordInVisit ? siteVisits.previous('irrigation', working) : null,
  )
  const [showLastVisit, setShowLastVisit] = React.useState(true)
  const lastProblems =
    canRecordFindings && lastVisit?.status === 'ready'
      ? lastVisit.findings
      : null
  const lastVisitPins = React.useMemo(
    () =>
      lastProblems === null ? [] : lastVisitPinsOf(lastProblems, survey.site),
    [lastProblems, survey.site],
  )
  const areas = useAreas(siteId)
  // A campus with several timers and no areas drawn: timers cannot be placed
  // in the parks the crew thinks in until the parks are drawn. Only once the
  // areas have loaded, so a slow read does not flash the prompt.
  const needsParks =
    !viewingEarlier &&
    survey.site.timers.length > 1 &&
    areas.data !== undefined &&
    areas.data.areas.length === 0
  const geo = useGeolocation(true)
  const isDesktop = useMediaQuery(DESKTOP_QUERY)

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const mapRef = React.useRef<L.Map | null>(null)
  const markersRef = React.useRef(new Map<string, L.Marker>())
  const signaturesRef = React.useRef(new Map<string, string>())
  /** What each pin's cluster shows of it, read when a cluster is drawn. */
  const membersRef = React.useRef(new WeakMap<L.Marker, ClusterMember>())
  /**
   * The handlers bound to each pin, so the next pass removes exactly those.
   * A bare `off('dragend')` would also strip the cluster group's own.
   */
  const handlersRef = React.useRef(
    new Map<string, { click: () => void; dragend: () => void }>(),
  )
  /** Where each pin was last put, as "latitude,longitude". */
  const placedAtRef = React.useRef(new Map<string, string>())
  const clustersRef = React.useRef<L.MarkerClusterGroup | null>(null)
  const outlinesRef = React.useRef<L.LayerGroup | null>(null)
  const regionLabelsRef = React.useRef<RegionLabelLayer | null>(null)
  /**
   * What a tap on the map may pick: the zones as drawn, and whether a tap
   * picks one now. Not while placing or moving a pin, when a tap on the map
   * changing the zone being walked would change where the pin goes.
   */
  const zoneTapRef = React.useRef<{ zones: DrawnZone[]; enabled: boolean }>({
    zones: [],
    enabled: true,
  })
  const meRef = React.useRef<{
    dot: L.CircleMarker
    accuracy: L.Circle
  } | null>(null)
  const framedRef = React.useRef(false)

  const [sheet, setSheet] = React.useState<SheetTarget | null>(null)
  const [placing, setPlacing] = React.useState<PlaceRequest | null>(null)
  /**
   * The pin being moved, and where it has been dragged to. Dragging is off
   * until somebody chooses "Move pin", as on the tree survey: an always
   * draggable marker lets a thumb meant to pan the map quietly move a valve
   * that was already in the right place.
   */
  const [moving, setMoving] = React.useState<{
    key: string
    at: { latitude: number; longitude: number } | null
  } | null>(null)
  /**
   * The zone whose outline is being drawn. A mode like Move pin: the sheet
   * gives way to a bar, taps on the map add corners, and Save writes the
   * outline through the queue like any other change to the zone.
   */
  const [outliningId, setOutliningId] = React.useState<string | null>(null)
  const [addOpen, setAddOpen] = React.useState(false)
  const [currentZoneId, setCurrentZoneId] = React.useState<string | null>(null)
  const organization = useOrganization()
  const bidTray = useBidTray()
  /*
   * What the irrigation on the bid comes to: this site as the server prices
   * its synced repairs, beside the bid's other sites (docs/irrigation.md,
   * "The bid total").
   */
  const bidTotal = useIrrigationBidTotal({
    siteId,
    site: survey.site,
    canPrice: siteVisits.isSent,
    organization: organization.data,
    requestedProposalId: bidTray.requestedProposalId,
  })
  /*
   * On a phone the tray drops over the top of the map, which has no room for
   * it and an editor sheet at once. Opening a record or placing one closes
   * it; the header button brings it back.
   */
  const closeBidTray = bidTray.close
  React.useEffect(() => {
    if (!isDesktop && (sheet !== null || placing !== null)) closeBidTray()
  }, [isDesktop, sheet, placing, closeBidTray])
  const [follow, setFollow] = React.useState(false)
  const [notice, setNotice] = React.useState<string | null>(null)

  // Read by effects that should run on a selection change, not on every
  // redraw of the site. Synced after render rather than written during it.
  const siteRef = React.useRef(survey.site)
  React.useLayoutEffect(() => {
    siteRef.current = survey.site
  })

  const shown = liveTarget(sheet, survey.site)
  const selectedKey = targetKey(shown)
  // A move outlives its record only if the record vanished mid-drag (removed
  // on another phone); it is then inert rather than holding the screen.
  const move = moving !== null && moving.key === selectedKey ? moving : null
  // Inert, as a move is, if the zone vanished while it was being outlined.
  const outlineZone =
    outliningId === null
      ? null
      : (survey.site.zones.find((zone) => zone.id === outliningId) ?? null)
  const outline = useBoundaryDraft(outlineZone?.boundary ?? null)
  // Read by the map's own handlers, which are bound once with the map.
  const outlineRef = React.useRef(outline)
  React.useLayoutEffect(() => {
    outlineRef.current = outline
  })
  React.useLayoutEffect(() => {
    zoneTapRef.current.enabled = placing === null && moving === null
  })
  const outlineLayerRef = React.useRef<BoundaryLayer | null>(null)
  const zones = zonesInOrder(survey.site)
  const currentZone = zones.find((zone) => zone.id === currentZoneId) ?? null
  // A problem at a spot goes into the zone whose sheet asked for it, or else
  // the zone being walked.
  const placingZoneId = placing?.zoneId ?? currentZoneId
  const placingZone = zones.find((zone) => zone.id === placingZoneId) ?? null

  /* -- the map ------------------------------------------------------- */

  React.useEffect(() => {
    const container = containerRef.current
    if (container === null) return undefined
    const markers = markersRef.current
    const signatures = signaturesRef.current
    const handlers = handlersRef.current
    const members = membersRef.current
    const placedAt = placedAtRef.current
    const map = createSiteMap(container)
    framedRef.current = false
    outlinesRef.current = L.layerGroup().addTo(map)
    /*
     * Pins close enough to cover each other are drawn as one (see "Crowded
     * pins" in docs/irrigation.md). A tap on one is handled below, not by
     * the group, which zooms until every cluster inside has parted and so
     * goes to the deepest, blurriest zoom whenever two pins inside share a
     * spot.
     */
    const clusters = L.markerClusterGroup({
      maxClusterRadius: CLUSTER_RADIUS_PX,
      zoomToBoundsOnClick: false,
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      spiderfyDistanceMultiplier: 1.2,
      spiderLegPolylineOptions: {
        className: 'stroke-map-halo',
        weight: 1.5,
        opacity: 0.9,
      },
      animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      iconCreateFunction: (cluster) =>
        L.divIcon({
          html: irrigationClusterHtml({
            members: cluster.getAllChildMarkers().flatMap((marker) => {
              const member = members.get(marker)
              return member === undefined ? [] : [member]
            }),
          }),
          className: '',
          iconSize: [CLUSTER_SIZE_PX, CLUSTER_SIZE_PX],
          iconAnchor: [CLUSTER_SIZE_PX / 2, CLUSTER_SIZE_PX / 2],
        }),
    }).addTo(map)
    // Zoom in just far enough for the pins to part, or fan them out around
    // their spot when no zoom would. A tap on the middle of a fan closes it.
    let fanned: L.MarkerCluster | null = null
    clusters.on('spiderfied', (event) => {
      fanned = event.cluster
    })
    clusters.on('unspiderfied', () => {
      fanned = null
    })
    clusters.on('clusterclick', (event: L.LeafletEvent) => {
      const cluster = event.propagatedFrom
      if (!(cluster instanceof L.MarkerCluster)) return
      if (cluster === fanned) {
        cluster.unspiderfy()
        return
      }
      const zoom = partingZoom(
        map,
        cluster.getAllChildMarkers().map((marker) => marker.getLatLng()),
      )
      if (zoom === null) {
        cluster.spiderfy()
      } else {
        map.fitBounds(cluster.getBounds(), { maxZoom: zoom, padding: [48, 48] })
      }
    })
    clustersRef.current = clusters
    regionLabelsRef.current = new RegionLabelLayer(map)
    // Panning means the person wants to look somewhere other than where they stand.
    map.on('dragstart', () => {
      setFollow(false)
    })
    mapRef.current = map
    // A zone's outline while it is drawn, with the boundary page's handles.
    const outlineLayer = new BoundaryLayer(map, {
      onMoveVertex: (index, point) => {
        outlineRef.current.moveVertex(index, point)
      },
      onInsertVertex: (afterIndex, point) => {
        outlineRef.current.insertVertex(afterIndex, point)
      },
      onSelectVertex: (index) => {
        outlineRef.current.selectVertex(index)
      },
    })
    outlineLayerRef.current = outlineLayer
    map.on('click', (event: L.LeafletMouseEvent) => {
      const draft = outlineRef.current
      if (draft.mode === 'drawing') {
        draft.appendVertex({
          latitude: event.latlng.lat,
          longitude: event.latlng.lng,
        })
      } else if (draft.mode === 'editing') {
        // Bare map while editing means "nothing selected", as on the
        // boundary page.
        draft.selectVertex(null)
      } else if (zoneTapRef.current.enabled) {
        // A tap inside a zone's outline makes it the zone being walked.
        const zone = zoneAt(
          { latitude: event.latlng.lat, longitude: event.latlng.lng },
          zoneTapRef.current.zones,
        )
        if (zone !== null) {
          setNotice(null)
          setCurrentZoneId(zone.id)
        }
      }
    })
    /*
     * The panel under the map changes height (the bar, the placement bar, a
     * sheet), which resizes the map without resizing the window, and Leaflet
     * only notices the window. Without this its idea of the center goes
     * stale, and "Place here" would drop a pin somewhere other than under the
     * crosshair.
     */
    const resize = new ResizeObserver(() => {
      map.invalidateSize({ pan: false })
    })
    resize.observe(container)
    return () => {
      resize.disconnect()
      outlineLayer.remove()
      outlineLayerRef.current = null
      regionLabelsRef.current?.remove()
      regionLabelsRef.current = null
      map.remove()
      mapRef.current = null
      outlinesRef.current = null
      clustersRef.current = null
      meRef.current = null
      markers.clear()
      signatures.clear()
      handlers.clear()
      placedAt.clear()
    }
  }, [])

  /* -- the site outline, areas and zone outlines, for context ---------- */

  React.useEffect(() => {
    const group = outlinesRef.current
    if (group === null) return
    group.clearLayers()
    // On an earlier visit, the outlines as they stood then.
    const boundary = asOf === null ? (site?.boundary ?? null) : asOf.boundary
    if (boundary !== null) {
      L.polygon(
        boundary.vertices.map((vertex) =>
          L.latLng(vertex.latitude, vertex.longitude),
        ),
        {
          className: 'stroke-map-halo',
          weight: 2,
          fill: false,
          dashArray: '6 4',
          interactive: false,
        },
      ).addTo(group)
    }
    /*
     * An area is a park: outlined in white and named, so a timer's pin is
     * seen in it. White rather than a color of its own, so a park never
     * shares one with a zone inside it (docs/map-regions.md). Outline only:
     * any fill over aerial imagery washes the turf out.
     */
    const parks = asOf === null ? (areas.data?.areas ?? []) : asOf.areas
    for (const area of parks) {
      L.polygon(
        area.boundary.vertices.map((vertex) =>
          L.latLng(vertex.latitude, vertex.longitude),
        ),
        {
          className: 'stroke-map-halo',
          weight: 2,
          fill: false,
          opacity: 0.9,
          interactive: false,
        },
      ).addTo(group)
    }
    /*
     * Zones in neighbor colors, each with its number. The zone being walked
     * is drawn solid and washed in its color; while there is one, the rest
     * stand back. Taken in timer and zone order, so a zone drawn later
     * leaves the colors before it as they were.
     */
    const timerOrder = new Map(
      survey.site.timers.map((timer, index) => [timer.id, index]),
    )
    const drawn: DrawnZone[] = [
      ...(asOf === null ? survey.site.zones : asOf.zones),
    ]
      .sort(
        (a, b) =>
          (timerOrder.get(a.timerId) ?? 0) - (timerOrder.get(b.timerId) ?? 0) ||
          a.number - b.number,
      )
      .flatMap((zone) =>
        // The zone being outlined is drawn by its editing layer instead.
        zone.boundary === null || zone.id === outliningId
          ? []
          : [
              {
                id: zone.id,
                number: zone.number,
                ring: zone.boundary.vertices,
              },
            ],
      )
    zoneTapRef.current.zones = drawn
    const colors = regionColors(
      drawn.map((zone) => ({ key: zone.id, ring: zone.ring })),
    )
    const walking = drawn.some((zone) => zone.id === currentZoneId)
    const labels: RegionLabelSpec[] = parks.map((area) => ({
      key: `area:${area.id}`,
      ring: area.boundary.vertices,
      label: { kind: 'name', text: area.name },
    }))
    for (const zone of drawn) {
      const color = colors.get(zone.id) ?? 'yellow'
      const current = zone.id === currentZoneId
      L.polygon(
        zone.ring.map((vertex) => L.latLng(vertex.latitude, vertex.longitude)),
        {
          className: current
            ? `${REGION_STROKE[color]} ${REGION_FILL[color]}`
            : REGION_STROKE[color],
          weight: current ? 3 : 2,
          opacity: walking && !current ? 0.35 : 1,
          fill: current,
          fillOpacity: 0.2,
          ...(current ? {} : { dashArray: '5 3' }),
          interactive: false,
        },
      ).addTo(group)
      labels.push({
        key: `zone:${zone.id}`,
        ring: zone.ring,
        label: {
          kind: 'number',
          text: String(zone.number),
          color,
          current,
          faded: walking && !current,
        },
        ...(current ? { always: true } : {}),
      })
    }
    regionLabelsRef.current?.set(labels)
  }, [
    site?.boundary,
    areas.data,
    survey.site.zones,
    survey.site.timers,
    currentZoneId,
    outliningId,
    asOf,
  ])

  /* -- on an earlier visit, the parts come out since ------------------ */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || asOf === null) return undefined
    const layer = L.layerGroup().addTo(map)
    for (const part of goneSinceOf(asOf, survey.site)) {
      L.marker(L.latLng(part.latitude, part.longitude), {
        interactive: false,
        icon: L.divIcon({
          html: irrigationMarkerHtml({
            tone: part.tone,
            ...(part.glyph === undefined ? {} : { glyph: part.glyph }),
            selected: false,
            pending: false,
            hasOpenProblem: false,
            faint: true,
          }),
          className: '',
          iconSize: [MARKER_SIZE_PX, MARKER_SIZE_PX],
          iconAnchor: [MARKER_SIZE_PX / 2, MARKER_SIZE_PX / 2],
        }),
      }).addTo(layer)
    }
    return () => {
      layer.remove()
    }
  }, [asOf, survey.site])

  /* -- a zone outline being drawn ------------------------------------ */

  React.useEffect(() => {
    outlineLayerRef.current?.render({
      vertices: outline.vertices,
      editable: outline.mode !== 'view',
      selectedIndex: outline.selectedIndex,
      invalid: outline.problem !== null,
      closed: outline.vertices.length >= 3,
      showMidpoints: outline.mode === 'editing',
    })
  }, [outline.vertices, outline.mode, outline.selectedIndex, outline.problem])

  /* -- pins ----------------------------------------------------------- */

  React.useEffect(() => {
    const map = mapRef.current
    const clusters = clustersRef.current
    if (map === null || clusters === null) return
    const markers = markersRef.current
    const signatures = signaturesRef.current
    const members = membersRef.current
    const handlers = handlersRef.current
    const placedAt = placedAtRef.current
    const seen = new Set<string>()
    const toCluster: L.Marker[] = []
    let clusterChanged = false
    const drawn = [
      ...(asOf === null
        ? pinsOf(survey.site)
        : pinsAsOf(pinsOf(survey.site), asOf)),
      ...(showLastVisit ? lastVisitPins : []),
    ]
    for (const pin of drawn) {
      seen.add(pin.key)
      const props: IrrigationMarkerProps = {
        ...pin.props,
        selected: pin.key === selectedKey,
        pending: survey.pendingKeys.has(pin.key),
      }
      const signature = irrigationMarkerSignature(props)
      const isMoving = move !== null && move.key === pin.key
      const at =
        isMoving && move.at !== null
          ? L.latLng(move.at.latitude, move.at.longitude)
          : L.latLng(pin.latitude, pin.longitude)
      const where = `${String(at.lat)},${String(at.lng)}`
      let marker = markers.get(pin.key)
      if (marker === undefined) {
        marker = L.marker(at, { keyboard: true, riseOnHover: true })
        markers.set(pin.key, marker)
      } else if (placedAt.get(pin.key) !== where) {
        // Only when it moved, since a pin in the group that moves is clustered
        // again. Checked against where it was put, not where it is drawn: a
        // cluster fanned out moves its pins for as long as it is open.
        marker.setLatLng(at)
      }
      placedAt.set(pin.key, where)
      // Before the pin joins the group, which draws its cluster as it does.
      members.set(marker, {
        tone: pin.props.tone,
        noun: pinNoun(pin),
        hasOpenProblem: pin.props.hasOpenProblem,
        pending: props.pending,
        faint: pin.props.faint === true,
      })
      /*
       * The selected pin stands on its own, never in a cluster: it is the one
       * being looked at, and the one Move pin drags. The rest go in the group,
       * which draws each as a pin or gathers it into a cluster.
       */
      const inGroup = clusters.hasLayer(marker)
      if (pin.key === selectedKey) {
        if (inGroup) clusters.removeLayer(marker)
        if (!map.hasLayer(marker)) marker.addTo(map)
      } else if (!inGroup) {
        marker.remove()
        toCluster.push(marker)
      }
      if (signatures.get(pin.key) !== signature) {
        marker.setIcon(
          L.divIcon({
            html: irrigationMarkerHtml(props),
            className: '',
            iconSize: [MARKER_SIZE_PX, MARKER_SIZE_PX],
            iconAnchor: [MARKER_SIZE_PX / 2, MARKER_SIZE_PX / 2],
          }),
        )
        signatures.set(pin.key, signature)
        clusterChanged = true
      }
      // Rebound every pass, so the handler never closes over an old record.
      const { target, key } = pin
      const placed = marker
      const bound = handlers.get(key)
      if (bound !== undefined) {
        marker.off('click', bound.click).off('dragend', bound.dragend)
      }
      const next = {
        click: () => {
          // Mid-move, a tap on another pin must not swap the record being moved.
          // Nor while an outline is drawn: a corner tapped near a pin is a corner.
          if (move !== null || outliningId !== null) return
          setAddOpen(false)
          setPlacing(null)
          setSheet(target)
        },
        dragend: () => {
          const point = placed.getLatLng()
          setMoving({ key, at: { latitude: point.lat, longitude: point.lng } })
        },
      }
      marker.on('click', next.click).on('dragend', next.dragend)
      handlers.set(key, next)
      // After setIcon, which rebuilds the element the drag handler binds to.
      if (isMoving) {
        marker.dragging?.enable()
      } else {
        marker.dragging?.disable()
      }
      // Last visit's sit under this visit's, which are what a tap is for.
      let zIndex = 0
      if (pin.key === selectedKey) zIndex = 1000
      else if (pin.props.faint === true) zIndex = -1000
      marker.setZIndexOffset(zIndex)
    }
    const gone: L.Marker[] = []
    for (const [key, marker] of markers) {
      if (!seen.has(key)) {
        if (clusters.hasLayer(marker)) gone.push(marker)
        else marker.remove()
        markers.delete(key)
        signatures.delete(key)
        handlers.delete(key)
        placedAt.delete(key)
      }
    }
    // In one go each way, which the group clusters far faster than one by one.
    if (gone.length > 0) clusters.removeLayers(gone)
    if (toCluster.length > 0) clusters.addLayers(toCluster)
    // A pin that looks different changes the cluster it is in.
    if (clusterChanged) clusters.refreshClusters()
  }, [
    survey.site,
    survey.pendingKeys,
    selectedKey,
    move,
    outliningId,
    showLastVisit,
    lastVisitPins,
    asOf,
  ])

  /* -- first meaningful view ------------------------------------------ */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || framedRef.current) return
    const pins = pinsOf(survey.site)
    if (pins.length > 0) {
      map.fitBounds(
        L.latLngBounds(
          pins.map((pin) => L.latLng(pin.latitude, pin.longitude)),
        ),
        { padding: [48, 48], maxZoom: 20 },
      )
      framedRef.current = true
      return
    }
    const boundary = site?.boundary ?? null
    if (boundary !== null) {
      map.fitBounds(
        L.latLngBounds(
          boundary.vertices.map((vertex) =>
            L.latLng(vertex.latitude, vertex.longitude),
          ),
        ),
        { padding: [48, 48], maxZoom: 19 },
      )
      framedRef.current = true
      return
    }
    if (geo.fix !== null) {
      map.setView(L.latLng(geo.fix.latitude, geo.fix.longitude), 20)
      framedRef.current = true
    }
  }, [survey.site, site?.boundary, geo.fix])

  /* -- the "you are here" dot ----------------------------------------- */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return
    const { fix } = geo
    if (fix === null) {
      meRef.current?.dot.remove()
      meRef.current?.accuracy.remove()
      meRef.current = null
      return
    }
    const here = L.latLng(fix.latitude, fix.longitude)
    if (meRef.current === null) {
      meRef.current = {
        accuracy: L.circle(here, {
          radius: fix.accuracyM,
          className: 'fill-brand/15 stroke-brand/40',
          weight: 1,
          interactive: false,
        }).addTo(map),
        dot: L.circleMarker(here, {
          radius: 6,
          weight: 2.5,
          className: 'fill-brand stroke-map-halo',
          interactive: false,
        }).addTo(map),
      }
    } else {
      meRef.current.accuracy.setLatLng(here).setRadius(fix.accuracyM)
      meRef.current.dot.setLatLng(here)
    }
    if (follow) map.panTo(here, { animate: true, duration: 0.4 })
  }, [geo, follow])

  /* -- keep the selected pin clear of the sheet ------------------------ */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || selectedKey === null) return
    const pin = pinsOf(siteRef.current).find(
      (candidate) => candidate.key === selectedKey,
    )
    if (pin === undefined) return
    const size = map.getSize()
    const insets = isDesktop
      ? { bottom: 0, right: 0 }
      : { bottom: size.y * 0.6, right: 0 }
    const visibleHeight = size.y - insets.bottom
    if (visibleHeight <= 0) return
    const zoom = map.getZoom()
    const target = map.project(L.latLng(pin.latitude, pin.longitude), zoom)
    const point = map.latLngToContainerPoint(
      L.latLng(pin.latitude, pin.longitude),
    )
    // Only move the map when the pin is actually hidden behind the sheet.
    if (point.y < visibleHeight - 24 && point.y > 24) return
    const center = target.add(L.point(0, size.y / 2 - visibleHeight / 2))
    map.panTo(map.unproject(center, zoom), { animate: true, duration: 0.3 })
  }, [selectedKey, isDesktop])

  /* -- bring a pin being moved into view ----------------------------- */

  // You cannot drag what you cannot see. The sheet giving way to the move bar
  // only grows the map downward, so a pin the map was panned away from, off
  // the top or a side, would stay off.
  const movingKey = move?.key ?? null
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || movingKey === null) return
    const pin = pinsOf(siteRef.current).find(
      (candidate) => candidate.key === movingKey,
    )
    if (pin === undefined) return
    const at = L.latLng(pin.latitude, pin.longitude)
    if (!map.getBounds().pad(-0.1).contains(at)) {
      map.panTo(at, { animate: true, duration: 0.3 })
    }
  }, [movingKey])

  /* -- actions -------------------------------------------------------- */

  const context: EditorContext = {
    site: survey.site,
    canRecordFindings,
    findingsLoading: canRecordInVisit && !survey.findingsReady,
    problemTypes,
    pendingKeys: survey.pendingKeys,
    isOnline: survey.isOnline,
    isRevisit,
    asksWhen,
    // The system is only changed as it stands now, not from an earlier visit.
    canEditRecord: !viewingEarlier,
    lastVisit: sheetLastVisit(lastVisit),
    commit: survey.commit,
    open: (target) => {
      setSheet(target)
    },
    close: () => {
      setSheet(null)
    },
    place: startPlacing,
    currentZoneId,
    setCurrentZoneId,
    move:
      viewingEarlier ||
      shown === null ||
      selectedKey === null ||
      !hasOwnPin(shown)
        ? null
        : () => {
            setNotice(null)
            setMoving({ key: selectedKey, at: null })
          },
    outline:
      viewingEarlier || shown === null || shown.entity !== 'zone' || shown.isNew
        ? null
        : () => {
            startOutline(shown.record)
          },
  }

  /** Show an earlier visit, or the newest again, leaving whatever was open. */
  function viewVisit(visitId: string | null): void {
    setViewedVisitId(visitId)
    setSheet(null)
    setPlacing(null)
    setMoving(null)
    setOutliningId(null)
    setAddOpen(false)
    setNotice(null)
  }

  /**
   * Hand the screen to drawing a zone's outline, from the zone's sheet or,
   * for the zone being walked, from the Add menu.
   */
  function startOutline(zone: ZoneDto): void {
    setNotice(null)
    setOutliningId(zone.id)
    if (zone.boundary === null) {
      outline.startDrawing()
    } else {
      outline.startEditing(zone.boundary.vertices)
    }
  }

  /**
   * Save the outline as drawn; a cleared one removes the zone's outline.
   * The first outline a zone has is the record being filled in, whatever
   * visit it is drawn on.
   */
  function saveOutline(isCorrection?: boolean, changedOn?: string): void {
    if (outlineZone === null) return
    const boundary = outline.toSaveValue()
    const result = survey.commit(
      {
        kind: 'upsert',
        entity: 'zone',
        record: {
          ...outlineZone,
          boundary,
          updatedAt: new Date().toISOString(),
        },
      },
      outlineZone.boundary === null ? true : isCorrection,
      changedOn,
    )
    if (!result.ok) {
      setNotice(result.message)
      return
    }
    outline.reset(boundary)
    setNotice(null)
    setOutliningId(null)
  }

  function cancelOutline(): void {
    outline.cancel()
    setNotice(null)
    setOutliningId(null)
  }

  /** Draw the outline of the zone being walked, from the Add menu. */
  function outlineCurrentZone(): void {
    setAddOpen(false)
    setSheet(null)
    if (currentZone === null) {
      setNotice('Choose the zone you are walking first, then draw its outline.')
      return
    }
    startOutline(currentZone)
  }

  function startPlacing(request: PlaceRequest): void {
    setAddOpen(false)
    setSheet(null)
    setNotice(null)
    if (request.kind === 'problem' && !canRecordFindings) {
      setNotice(
        canRecordInVisit
          ? "This visit's problems are still loading. Try again in a moment."
          : 'Problems belong to a visit. Start one to record them.',
      )
      return
    }
    if (
      request.kind === 'problem' &&
      (request.zoneId ?? currentZoneId) === null
    ) {
      setNotice(
        'Choose the zone you are walking first: a problem at a spot goes into it.',
      )
      return
    }
    setPlacing(request)
  }

  /**
   * Drop what is being placed at the crosshair. A record that is complete
   * with its defaults is saved straight away, so dropping thirty valves is
   * thirty taps; one that needs more (a problem, "something else") opens as
   * a draft to finish.
   */
  function placeHere(isCorrection?: boolean, changedOn?: string): void {
    const map = mapRef.current
    if (map === null || placing === null) return
    const pin = pinAtCenter(map, geo.fix)
    const { site: current } = survey
    const { waterSourceId = null, timerId = null } = placing
    let target: SheetTarget
    switch (placing.kind) {
      case 'waterSource':
        target = {
          entity: 'waterSource',
          record: newWaterSource(siteId, pin),
          isNew: true,
        }
        break
      case 'timer':
        target = {
          entity: 'timer',
          record: newTimer(siteId, pin, waterSourceId),
          isNew: true,
        }
        break
      case 'problem': {
        if (placingZone === null) return
        target = {
          entity: 'problem',
          record: newProblem(siteId, {
            kind: 'zone',
            zoneId: placingZone.id,
            pin,
          }),
          isNew: true,
        }
        break
      }
      default:
        target = {
          entity: 'equipment',
          record: newEquipment(current, placing.kind, pin, {
            currentZoneId,
            waterSourceId,
            timerId,
          }),
          isNew: true,
        }
    }
    setPlacing(null)
    const change = savedOnDrop(target)
    if (change !== null && survey.commit(change, isCorrection, changedOn).ok) {
      if (target.entity === 'equipment') {
        valveForZoneWalked(target.record, isCorrection, changedOn)
      }
      setSheet({ ...target, isNew: false })
      return
    }
    setSheet(target)
  }

  /**
   * A valve box dropped while walking a zone with no valve holds that zone's
   * valve, as a valve dropped then would be that zone's. Untick it on the
   * box's sheet if it is not.
   */
  function valveForZoneWalked(
    box: IrrigationEquipmentDto,
    isCorrection?: boolean,
    changedOn?: string,
  ): void {
    if (box.kind !== 'VALVE_BOX' || currentZoneId === null) return
    const { site: current } = survey
    const zoneHasValve = current.equipment.some(
      (item) => item.kind === 'VALVE' && item.zoneId === currentZoneId,
    )
    if (zoneHasValve) return
    const valve = newEquipment(current, 'VALVE', pinOfRecord(box), {
      currentZoneId,
      boxId: box.id,
    })
    survey.commit(
      { kind: 'upsert', entity: 'equipment', record: valve },
      isCorrection,
      changedOn,
    )
  }

  function recenter(): void {
    const map = mapRef.current
    const { fix } = geo
    if (map === null || fix === null) return
    setFollow(true)
    map.setView(
      L.latLng(fix.latitude, fix.longitude),
      Math.max(map.getZoom(), 20),
    )
  }

  /* -- render ---------------------------------------------------------- */

  const backTo = site === undefined ? '/clients' : `/clients/${site.clientId}`
  const accuracyIsPoor = geo.fix !== null && geo.fix.accuracyM > ACCURACY_WARN_M

  const parent = shown === null ? null : parentOf(shown, survey.site)
  const editor =
    shown === null || move !== null || outlineZone !== null ? null : (
      <div className="space-y-3">
        {parent === null ? null : (
          <button
            type="button"
            className="-ml-1 flex items-center gap-0.5 rounded px-1 py-0.5 text-xs text-secondary-text hover:bg-subtle hover:text-primary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            onClick={() => {
              setSheet(parent)
            }}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {sheetTitle(parent, context)}
          </button>
        )}
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold">
            {sheetTitle(shown, context)}
          </h2>
          <button
            type="button"
            className="rounded px-2 py-1 text-sm text-secondary-text hover:bg-subtle"
            onClick={() => {
              setSheet(null)
            }}
          >
            Close
          </button>
        </div>
        <RecordEditor
          key={`${selectedKey ?? ''}:${String(shown.isNew)}`}
          target={shown}
          context={context}
        />
      </div>
    )

  /** Save where the pin was dragged to; a pin never dragged just stays. */
  function finishMove(isCorrection?: boolean, changedOn?: string): void {
    if (move === null || shown === null) return
    const change = move.at === null ? null : movedTo(shown, move.at)
    if (change !== null) {
      const result = survey.commit(change, isCorrection, changedOn)
      if (!result.ok) {
        setNotice(result.message)
        return
      }
    }
    setNotice(null)
    setMoving(null)
  }

  /**
   * The system first: a site with no water source starts there, and one
   * with sources shows each with its next step, before anybody walks zones.
   */
  function renderSystem(): React.ReactNode {
    if (survey.site.waterSources.length === 0) {
      // Placing one adds to the system, which an earlier visit does not.
      if (viewingEarlier) return null
      return (
        <div className="flex items-center gap-3 rounded-md border border-default px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Start with the water source</p>
            <p className="text-xs text-muted-text">
              The meter or well. The rest of the system follows from it, in the
              order the water runs.
            </p>
          </div>
          <Button
            type="button"
            className="h-11 shrink-0 lg:h-9"
            onClick={() => {
              startPlacing({ kind: 'waterSource' })
            }}
          >
            Place it
          </Button>
        </div>
      )
    }
    return (
      <SystemBar
        site={survey.site}
        onOpen={(target) => {
          setSheet(target)
        }}
      />
    )
  }

  /** What a redrawn outline asks on a revisit; a first outline asks nothing. */
  function outlineQuestion(): ChangeQuestion | null {
    if (!isRevisit || outlineZone === null || outlineZone.boundary === null) {
      return null
    }
    return editQuestion(
      'ZONE',
      zoneSnapshot(outlineZone),
      zoneSnapshot({ ...outlineZone, boundary: outline.toSaveValue() }),
    )
  }

  const movedRecord =
    shown === null || shown.entity === 'zones' ? null : TRACKED[shown.entity]
  const moveAsks =
    isRevisit && move?.at != null && movedRecord !== null
      ? moveQuestion(movedRecord)
      : null
  const placedKind = placing === null ? null : placedRecord(placing.kind)
  const placeAsks =
    isRevisit && placedKind !== null ? addQuestion(placedKind) : null

  function renderBottom(): React.ReactNode {
    if (outlineZone !== null) {
      return (
        <div className="space-y-2 p-3 lg:max-w-xl">
          <div>
            <p className="text-sm font-semibold">
              Outline of {zoneName(outlineZone, survey.site.timers)}
            </p>
            <p className="text-xs text-muted-text">{outlineHint(outline)}</p>
          </div>
          {outline.problem === null ? null : (
            <p role="alert" className="text-sm text-danger">
              {BOUNDARY_PROBLEM_MESSAGES[outline.problem]}
            </p>
          )}
          {notice === null ? null : (
            <p role="alert" className="text-sm text-danger">
              {notice}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <BoundaryDraftBar
              draft={outline}
              hasShape={outline.vertices.length >= BOUNDARY_MIN_VERTICES}
              saveDisabled={!outline.canSave}
              pending={false}
              onDiscard={cancelOutline}
              onSave={saveOutline}
              question={outlineQuestion()}
              askWhen={asksWhen}
            />
          </div>
        </div>
      )
    }
    if (move !== null && shown !== null) {
      return (
        <div className="space-y-2 p-3 lg:max-w-xl">
          <div>
            <p className="text-sm font-semibold">
              Moving {sheetTitle(shown, context)}
            </p>
            <p className="text-xs text-muted-text">
              {move.at === null
                ? 'Drag the pin to where it really is.'
                : 'Done keeps the new spot. Cancel puts it back.'}
            </p>
          </div>
          {notice === null ? null : (
            <p role="alert" className="text-sm text-danger">
              {notice}
            </p>
          )}
          <div className="flex gap-2">
            {moveAsks === null ? (
              <Button
                type="button"
                className="h-11 flex-1 lg:h-9"
                onClick={() => {
                  finishMove()
                }}
              >
                Done
              </Button>
            ) : (
              <ChangeAnswers
                question={moveAsks}
                className={cn(
                  'min-w-0 flex-1 [&>button]:flex-1',
                  asksWhen ? null : 'flex-nowrap',
                )}
                buttonClassName="h-11 px-2 lg:h-9"
                askWhen={asksWhen}
                onAnswer={finishMove}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              className="h-11 lg:h-9"
              onClick={() => {
                setNotice(null)
                setMoving(null)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )
    }
    if (placing !== null) {
      return (
        <div className="space-y-2 p-3">
          <p className="text-sm">
            Move the map until the crosshair is on the{' '}
            {PLACE_LABELS[placing.kind].toLowerCase()}
            {placing.kind === 'problem' && placingZone !== null
              ? ` in ${zoneName(placingZone, survey.site.timers)}`
              : ''}
            .
          </p>
          <div className="flex gap-2">
            {placeAsks === null ? (
              <Button
                type="button"
                className="flex-1"
                onClick={() => {
                  placeHere()
                }}
              >
                Place here
              </Button>
            ) : (
              // A revisit: placing it says whether it is new or was missed.
              <ChangeAnswers
                question={placeAsks}
                className={cn(
                  'min-w-0 flex-1 [&>button]:flex-1',
                  asksWhen ? null : 'flex-nowrap',
                )}
                buttonClassName="px-2"
                askWhen={asksWhen}
                onAnswer={placeHere}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setPlacing(null)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )
    }
    if (!isDesktop && editor !== null) {
      return <div className="max-h-[60svh] overflow-y-auto p-4">{editor}</div>
    }
    return (
      <div className="space-y-2 p-3 lg:max-w-xl">
        {notice === null ? null : (
          <p className="text-sm text-warning">{notice}</p>
        )}
        {addOpen ? (
          <div className="space-y-3">
            {ADD_GROUPS.map((group) => (
              <div key={group.title} className="space-y-1.5">
                <p className="text-xs font-medium text-secondary-text">
                  {group.title}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {group.kinds.map((kind) => (
                    <Button
                      key={kind}
                      type="button"
                      variant="outline"
                      className="h-11 justify-start lg:h-9"
                      onClick={() => {
                        startPlacing({ kind })
                      }}
                    >
                      {PLACE_LABELS[kind]}
                    </Button>
                  ))}
                  {group.zoneOutline === true ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 justify-start lg:h-9"
                      onClick={outlineCurrentZone}
                    >
                      {currentZone === null || currentZone.boundary === null
                        ? 'Draw zone outline'
                        : 'Edit zone outline'}
                    </Button>
                  ) : null}
                </div>
                {group.hint === undefined ? null : (
                  <p className="text-xs text-muted-text">{group.hint}</p>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setAddOpen(false)
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <>
            {renderSystem()}
            <div className="flex items-end gap-2">
              <label className="min-w-0 flex-1 space-y-1">
                <span className="block text-xs font-medium text-secondary-text">
                  Walking
                </span>
                <select
                  className="h-11 w-full rounded-md border border-default bg-surface px-2 text-sm lg:h-9"
                  value={currentZoneId ?? ''}
                  onChange={(event) => {
                    setNotice(null)
                    setCurrentZoneId(
                      event.target.value === '' ? null : event.target.value,
                    )
                  }}
                >
                  <option value="">
                    {zones.length === 0 ? 'No zones yet' : 'No zone'}
                  </option>
                  {survey.site.timers.length <= 1
                    ? zones.map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zoneName(zone, survey.site.timers)}
                        </option>
                      ))
                    : zonesByTimer(survey.site).map((group) => (
                        <optgroup key={group.timerId} label={group.label}>
                          {group.zones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zoneName(zone, survey.site.timers)}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                </select>
              </label>
              {currentZone === null ? null : (
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 lg:h-9"
                  onClick={() => {
                    setSheet({
                      entity: 'zone',
                      record: currentZone,
                      isNew: false,
                    })
                  }}
                >
                  Zone
                </Button>
              )}
              {/* Problems at a spot are added from their zone's sheet then. */}
              {viewingEarlier ? null : (
                <Button
                  type="button"
                  className="h-11 lg:h-9"
                  onClick={() => {
                    setNotice(null)
                    setAddOpen(true)
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              )}
            </div>
          </>
        )}
        {addOpen ? null : (
          <RepairEstimate site={survey.site} enabled={siteVisits.isSent} />
        )}
      </div>
    )
  }

  /** The bid tray, as a sheet over the map or a column beside it. */
  function renderBidTray(
    variant: 'overlay' | 'column',
    className?: string,
  ): React.ReactNode {
    return (
      <IrrigationBidTray
        id={BID_TRAY_ID}
        total={bidTotal}
        siteName={site?.name ?? 'This property'}
        variant={variant}
        queuedCount={survey.queuedCount}
        isOnline={survey.isOnline}
        className={className}
        onClose={bidTray.toggle}
        onChooseProposal={bidTray.chooseProposal}
      />
    )
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-app">
      <header className="flex items-center gap-2 border-b border-default bg-surface px-3 py-2">
        <Link
          to={backTo}
          className="rounded p-1.5 text-secondary-text hover:bg-subtle"
          aria-label="Back to client"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {site?.name ?? 'Irrigation'}
          </p>
          <p className="text-xs text-muted-text">Irrigation</p>
        </div>
        <BidTotalButton
          totalCents={bidTotal.totalCents}
          isIncomplete={bidTotal.isIncomplete}
          editedCents={bidTotal.siteCents}
          isLoading={bidTotal.bidContext.isResolving || bidTotal.isLoading}
          open={bidTray.open}
          onToggle={bidTray.toggle}
          controls={BID_TRAY_ID}
        />
        <SyncChip survey={survey} />
      </header>
      <VisitBar
        kind="irrigation"
        visits={siteVisits}
        working={working}
        unlockedId={unlockedVisitId}
        onUnlock={setUnlockedVisitId}
        isOnline={survey.isOnline}
        onView={viewVisit}
        viewingEarlier={viewingEarlier}
      />
      {viewingEarlier && asOfQuery.isError ? (
        <p className="border-b border-default bg-surface px-3 py-1.5 text-xs text-secondary-text">
          Where things stood then needs a signal, so the system is drawn as it
          stands now.
        </p>
      ) : null}
      {needsParks ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-default bg-surface px-3 py-1.5 text-xs">
          <span className="min-w-0 text-secondary-text">
            No areas drawn yet. Draw the site’s parks or sections, and each
            timer is grouped under the one it stands in.
          </span>
          <Link
            to={`/sites/${siteId}/boundary?from=irrigation`}
            className="ml-auto shrink-0 font-medium text-brand underline-offset-2 hover:underline"
          >
            Draw areas
          </Link>
        </div>
      ) : null}
      {survey.lostOperations > 0 ? (
        <p
          role="alert"
          className="border-b border-default bg-surface px-3 py-2 text-sm text-danger"
        >
          {survey.lostOperations} unsynced{' '}
          {survey.lostOperations === 1 ? 'change' : 'changes'} from an older
          version of the app could not be read. A copy was kept on this phone.
        </p>
      ) : null}
      <div className="relative flex min-h-0 flex-1">
        {/*
          The bid total, beside the map on a wide screen, opposite the
          editor, so both stay open at once.
        */}
        {bidTray.open && isDesktop ? (
          <aside className="w-[var(--ds-side-tray)] shrink-0">
            {renderBidTray('column')}
          </aside>
        ) : null}
        <div className="relative min-w-0 flex-1">
          <div ref={containerRef} className="absolute inset-0" />
          {bidTray.open && !isDesktop
            ? renderBidTray('overlay', 'absolute inset-x-0 top-0 z-[600]')
            : null}
          {placing === null ? null : (
            <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center">
              <Crosshair className="h-10 w-10 text-brand drop-shadow-[0_1px_2px_var(--ds-map-marker-halo)]" />
            </div>
          )}
          <div className="absolute left-3 top-3 z-[500] flex items-center gap-2 rounded-md bg-surface/90 px-2 py-1 shadow-sm">
            <span
              className={cn(
                'flex items-center gap-1 text-xs',
                accuracyIsPoor ? 'text-warning' : 'text-muted-text',
              )}
            >
              {accuracyIsPoor ? (
                <TriangleAlert className="h-3.5 w-3.5" />
              ) : (
                <Crosshair className="h-3.5 w-3.5" />
              )}
              {geo.fix === null
                ? 'Waiting for GPS'
                : `GPS ±${String(Math.round(geo.fix.accuracyM))} m`}
            </span>
            <button
              type="button"
              className="rounded p-1 text-secondary-text hover:bg-subtle disabled:opacity-40"
              aria-label="Center on me"
              disabled={geo.fix === null}
              onClick={recenter}
            >
              <LocateFixed className="h-4 w-4" />
            </button>
            {lastVisitPins.length === 0 ? null : (
              <button
                type="button"
                aria-pressed={showLastVisit}
                className={cn(
                  'flex items-center gap-1 rounded border-l border-default py-1 pr-1 pl-2 text-xs hover:bg-subtle',
                  showLastVisit ? 'text-primary-text' : 'text-muted-text',
                )}
                onClick={() => {
                  setShowLastVisit((shown) => !shown)
                }}
              >
                <History className="h-3.5 w-3.5" />
                Last visit · {lastVisitPins.length}
              </button>
            )}
          </div>
          {isDesktop ? (
            <MapZoomControls
              className="absolute right-3 top-3 z-[500]"
              onZoomIn={() => {
                mapRef.current?.zoomIn()
              }}
              onZoomOut={() => {
                mapRef.current?.zoomOut()
              }}
            />
          ) : null}
        </div>
        {isDesktop && editor !== null ? (
          <aside className="w-[var(--ds-inspector)] shrink-0 overflow-y-auto border-l border-default bg-surface p-4">
            {editor}
          </aside>
        ) : null}
      </div>
      {/*
        Under the map rather than floating over it, as the tree survey does:
        the map's attribution sits in its bottom corner and has to stay
        visible (the imagery's terms), and a bar laid over it would either
        hide it or have its taps swallowed by it.
      */}
      <div className="shrink-0 border-t border-default bg-surface">
        {renderBottom()}
      </div>
    </div>
  )
}

/** Saved, waiting, offline, or refused: what has and has not reached the server. */
function SyncChip({ survey }: { survey: IrrigationSurvey }) {
  const [confirming, setConfirming] = React.useState(false)
  if (survey.syncError?.needsAttention === true) {
    return (
      <div className="flex items-center gap-1.5">
        <span
          className="flex items-center gap-1 text-xs text-danger"
          title={survey.syncError.message}
        >
          <TriangleAlert className="h-3.5 w-3.5" />
          Not syncing
        </span>
        {confirming ? (
          <>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={survey.discardQueue}
            >
              Discard {survey.queuedCount}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setConfirming(false)
              }}
            >
              Keep
            </Button>
          </>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setConfirming(true)
            }}
          >
            Fix
          </Button>
        )}
      </div>
    )
  }
  if (survey.queuedCount === 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-text">
        <Check className="h-3.5 w-3.5" />
        Saved
      </span>
    )
  }
  if (!survey.isOnline) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-text">
        <CloudOff className="h-3.5 w-3.5" />
        {survey.queuedCount} on this phone
      </span>
    )
  }
  return (
    <span className="flex items-center gap-1 text-xs text-muted-text">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      {survey.queuedCount} to sync
    </span>
  )
}
